import {
  useMediaQuery,
  Box,
  Grid,
  Button,
  Card,
} from '@mui/material'
import MainHeader from '../shared/organism/MainHeader'
import React, { useState } from 'react'
import { theme } from '../../theme/DashboardTheme'
import PageLayout from './PageLayout'
import CommunicationPreference from '../Views/SettingsViews/CommunicationPreference'
import AccSecurity from '../shared/molecule/AccSecurity'
import { useLocation, useNavigate, Outlet } from 'react-router-dom'

interface TabBarProps {
  selectedTab: number
  onUpdatePreferences?: () => void
  preferenceState: { hasChanged: boolean; updating: boolean }
}

const TabBar: React.FC<TabBarProps> = ({ selectedTab, onUpdatePreferences, preferenceState }) => {
  const navigate = useNavigate()
  const tabs = [
    { label: 'Account & Security', path: '/settings/account-security' },
    { label: 'Communication Preferences', path: '/settings/communication-preferences' }
  ]

  const isActive = preferenceState.hasChanged && !preferenceState.updating

  return (
    <Grid
      container
      sx={{
        paddingTop: 3,
        marginBottom: 3,
      }}>
      <Grid
        container
        item
        md={12}
        xs={12}
        sx={{
          display: 'flex',
          alignItems: 'flex-end'
        }}>
        <Grid item sm={12} md={8}>
          <Box sx={{ display: 'flex', gap: 3, paddingLeft: 0 }}>
            {tabs.map((tab, index) => (
              <Box
                key={index}
                onClick={() => navigate(tab.path)}
                sx={{
                  px: { md: 1, lg: 2 },
                  height: '65px',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  color: index === selectedTab ? '#9135E0' : '#6B7280',
                  fontSize: { sm: 15, md: 18, lg: 20 },
                  fontFamily: index === selectedTab ? 'InterMedium' : 'InterRegular',
                  fontWeight: index === selectedTab ? 500 : 400,
                  borderBottom: index === selectedTab ? '2px solid #9135E0' : 'none',
                }}>
                {tab.label}
              </Box>
            ))}
          </Box>
        </Grid>
        {selectedTab === 1 && (
          <Grid item sm={12} md={4} mt={{ sm: 3, md: 0 }} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Box
              onClick={isActive ? onUpdatePreferences : undefined}
              sx={{
                height: { sm: '45px', md: '60px' },
                borderRadius: '10px',
                background: '#9135E0',
                color: '#fff',
                fontFamily: 'InterMedium',
                fontSize: { sm: '13px', md: '14px', lg: '16px' },
                fontWeight: 500,
                maxWidth: { sm: '210px', md: '240px' },
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                userSelect: 'none',
                opacity: isActive ? 1 : 0.6,
                cursor: isActive ? 'pointer' : 'default',
                pointerEvents: isActive ? 'auto' : 'none',
              }}>
              {preferenceState.updating ? 'Updating...' : 'Update your Preferences'}
            </Box>
          </Grid>
        )}
      </Grid>
    </Grid>
  )
}

const mobileTabs = ['Account & Security', 'Communication Preferences']

export const SettingsView = () => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const location = useLocation()
  const [updatePreferencesFn, setUpdatePreferencesFn] = useState<(() => Promise<void>) | null>(null)
  const [preferenceState, setPreferenceState] = useState<{ hasChanged: boolean; updating: boolean }>({
    hasChanged: false,
    updating: false
  })
  const [mobileTab, setMobileTab] = useState(0)

  const selectedTab = location.pathname.includes('communication-preferences') ? 1 : 0

  const registerUpdateFunction = (fn: () => Promise<void>) => {
    setUpdatePreferencesFn(() => fn)
  }

  const handleStateChange = (state: { hasChanged: boolean; updating: boolean }) => {
    setPreferenceState(state)
  }

  const handleUpdatePreferences = () => {
    if (updatePreferencesFn) {
      updatePreferencesFn()
    }
  }

  return (
    <PageLayout>
      <MainHeader title={'Settings'} />
      {isMobile ? (
        <Box>
          {/* Pill-style tab bar */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            height: '51px',
            bgcolor: '#fff',
            borderRadius: '16px',
            p: 0.5,
            px: 2,
            mt: isMobile ? 0 : 2,
            mb: 3,
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
          }}>
            {mobileTabs.map((tab, index) => {
              const isActive = mobileTab === index
              return (
                <Button
                  key={index}
                  onClick={() => setMobileTab(index)}
                  sx={{
                    border: 'none',
                    width: `${100 / mobileTabs.length}%`,
                    borderRadius: '12px',
                    height: '35px',
                    fontSize: { xs: '12px', sm: '14px' },
                    '@media (min-width:320px) and (max-width:374px)': { fontSize: '8px', lineHeight: '14px' },
                    '@media (min-width:375px) and (max-width:425px)': { fontSize: '10px', lineHeight: '14px' },
                    fontFamily: 'InterSemiBold',
                    textTransform: 'none',
                    bgcolor: isActive ? '#9135E0' : 'transparent',
                    color: isActive ? '#fff' : '#121212',
                    '&:hover': {
                      bgcolor: isActive ? '#614BBA' : 'rgba(116, 89, 217, 0.05)',
                    },
                  }}
                >
                  {tab}
                </Button>
              )
            })}
          </Box>

          {/* Tab content */}
          <Card sx={{
            px: { xs: 2, sm: 4 },
            py: { xs: 3, sm: 5 },
            borderRadius: 5,
            boxShadow: 'none',
            mb: 3,
          }}>
            {mobileTab === 0 && <AccSecurity />}
            {mobileTab === 1 && (
              <CommunicationPreference
                registerUpdateFunction={registerUpdateFunction}
                onStateChange={handleStateChange}
              />
            )}
          </Card>

          {/* Update Preferences button — outside the white card, only on Comm Prefs tab */}
          {mobileTab === 1 && (
            <Box sx={{ my: 3 }}>
              <Button
                disabled={!preferenceState.hasChanged || preferenceState.updating}
                onClick={handleUpdatePreferences}
                sx={{
                  width: '100%',
                  height: '50px',
                  backgroundColor: '#9135E0 !important',
                  borderRadius: '10px',
                  color: '#fff',
                  fontFamily: 'InterMedium',
                  fontSize: '14px',
                  fontWeight: 500,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#7a2bbf !important',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#9135E0 !important',
                    color: '#fff',
                    opacity: 0.5,
                  },
                }}>
                {preferenceState.updating ? 'Updating...' : 'Update your Preferences'}
              </Button>
            </Box>
          )}
        </Box>
      ) : (
        <Box sx={{ mb: '20px' }}>
          <TabBar
            selectedTab={selectedTab}
            onUpdatePreferences={handleUpdatePreferences}
            preferenceState={preferenceState}
          />
          <Outlet context={{ registerUpdateFunction, onStateChange: handleStateChange }} />
        </Box>
      )}
    </PageLayout>
  )
}
